import { CircularProgress } from '@mui/material'
import { GET_ORGANIZATION_BY_USER, getOrganizationsByUser } from 'api'
import clsx from 'clsx'
import { Button, Input, InputRadioImage, Typography } from 'components'
import { MenuContext } from 'context'
import { useSearchDebounce } from 'hooks'
import {
	Icons,
	OrganizationsByUserData,
	SelectedOrganizationProps,
	TypographyTypes
} from 'interfaces'
import React, { useContext, useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import Cookies from 'universal-cookie'
import { configGenaral } from 'utils'

export const SelectedOrganization: React.FC<SelectedOrganizationProps> = ({
	user,
	setStep
}): JSX.Element => {
	const { setFullName } = useContext(MenuContext)
	const cookies = new Cookies()
	const navigate = useNavigate()
	const [search, setSearch] = useState('')
	const [organizationSelected, setOrganizationSelected] =
		useState<OrganizationsByUserData>()
	const { inputSearch, handleChangeDebounce, resetInput } =
		useSearchDebounce('')
	const { data, isLoading } = useQuery(
		[GET_ORGANIZATION_BY_USER, inputSearch, user.token],
		() =>
			getOrganizationsByUser(
				encodeURIComponent(user.email as string),
				user.token as string,
				inputSearch
			)
	)

	useEffect(() => {
		if (data) {
			setOrganizationSelected(data.items[0])
		}
	}, [data])

	const handleSubmit = (): void => {
		const roles =
			organizationSelected?.roles.map(item => item.credential_id) || []

		setFullName(user.fullName as string)

		const cookieData = {
			fullName: user.fullName,
			userId: user.userId,
			organization_id: organizationSelected?.id,
			organization_name: organizationSelected?.name,
			organization_logo: organizationSelected?.icon_image_url,
			organization_slug: organizationSelected?.slug,
			roles,
			token: user.token
		}

		Object.entries(cookieData).forEach(([key, value]) => {
			cookies.set(key, value, { path: '/' })
		})

		navigate('/')
	}

	return (
		<div className="border border-white rounded-[16px]  p-6 min-w-[455px]">
			<Typography
				title="Welcome back!"
				fontLeading="22/27"
				type={TypographyTypes.h1}
				className="text-white mb-6 !font-normal"
			/>
			<div className="flex items-center gap-2 border-b border-gray-4 pb-4">
				<div className="h-14 w-14 flex justify-center items-center bg-gray-3 border-[4px] border-blue-primary rounded-full">
					<Typography
						title={user.fullName ? (user.fullName[0] as string) : ''}
						fontLeading="22/27"
						type={TypographyTypes.p}
						className="text-white"
					/>
				</div>
				<div>
					<Typography
						title={user.fullName ? (user.fullName as string) : ''}
						fontLeading="22/27"
						type={TypographyTypes.p}
						className="text-white !font-bold"
					/>
					<Typography
						title="Admin"
						fontLeading="22/27"
						type={TypographyTypes.p}
						className="text-white !font-normal"
					/>
				</div>
			</div>
			<Typography
				title="Please select the organization you want to manage:"
				fontLeading="12/15"
				type={TypographyTypes.p}
				className="text-white !font-normal py-4"
			/>
			<Input
				name="search"
				placeholder="Search Organization"
				className="w-full !p-[unset]"
				onChangeCustom={e => {
					setSearch(e.target.value)
					handleChangeDebounce(e)
				}}
				rightIcon={Icons.cancel}
				rightClick={() => {
					resetInput()
					setSearch('')
				}}
				value={search}
				maxLength={configGenaral.maxSearchValue}
				inputClassName="!p-[unset] !py-1 !px-2 border-white rounded-3xl "
				inputFieldClassName="text-white !placeholder-white"
				inputFocus
				borderFull={false}
				search
			/>
			<div className="pt-2 max-h-[230px] overflow-y-auto border-b border-gray-4 pb-2">
				{isLoading ? (
					<div className="flex items-center justify-center w-full h-[400px] py-2 px-1 ">
						<CircularProgress disableShrink />
					</div>
				) : (
					data?.items?.map(option => (
						<InputRadioImage
							key={option.id}
							option={option}
							defaultOptions={data.items[0].id}
							optionSelected={setOrganizationSelected}
							className="!text-white"
						/>
					))
				)}
			</div>
			<div className="flex flex-row justify-between mt-6">
				<Button
					label="Cancel"
					type="button"
					color="Transparent"
					className="min-h-[40px] !min-w-[124px] !max-w-[124px] !bg-transparent-default !text-white border-white"
					onClick={() => setStep(0)}
				/>
				<Button
					label="Log in"
					type="button"
					color="Primary"
					disabled={!organizationSelected}
					className={clsx(
						'min-h-[40px] !min-w-[124px] !max-w-[124px] !bg-white !border-[1px] border-blue-primary border-solid !text-black-1'
					)}
					onClick={handleSubmit}
				/>
			</div>
		</div>
	)
}
